import type { ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import type { RunJob } from '../lib/fleet-api.js';
import { jobUrl, OUTCOMES, outcomeOf, triggerLabel, type Outcome } from '../lib/fleet-runs.js';
import { seconds } from '../lib/fleet-view.js';
import { Button } from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';

/**
 * A job's details dialog, and the two small pieces it shares with Run history.
 *
 * Written for Run history (org-admin#111), and its own module since
 * org-admin#126 so Runners and queue opens the same dialog for a job running or
 * waiting now. fleet-api sends those jobs in the run-history shape for this.
 *
 * ── THE GITHUB LINK IS THE RE-RUN BUTTON ─────────────────────────────
 *
 * A stuck job is re-run on GitHub, whose job page already has the button, so the
 * portal has none of its own (Jon, 14 Sep, on #99). This dialog is how an admin
 * gets there from the queue.
 */

/** Status tokens: an outcome means good or bad. Running is the accent, not a status. */
export const COLOR: Record<Outcome, string> = {
  success: C.successDot,
  failure: C.danger,
  cancelled: C.muted,
  skipped: C.mutedDot,
  running: C.brand,
};

export const labelOf = (outcome: Outcome) => OUTCOMES.find((o) => o.key === outcome)?.label ?? outcome;

export const KIND_LABEL: Record<string, string> = { fleet: 'Your runners', blacksmith: 'Blacksmith', github: 'GitHub-hosted' };

/** The outcome in words beside a dot in the chart's own colour, so the list and the chart agree. */
export function OutcomeLabel({ outcome }: { outcome: Outcome }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', whiteSpace: 'nowrap', fontSize: '.8rem', fontWeight: 600, color: C.textMid }}>
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: COLOR[outcome], flex: 'none' }} />
      {labelOf(outcome)}
    </span>
  );
}

/** Repo and branch as one split pill: which codebase, then which line of it. */
export function RepoBranch({ repo, branch }: { repo: string; branch: string | null }) {
  return (
    <span style={s.pill} title={branch ? `${repo} · ${branch}` : repo}>
      <span style={s.pillRepo}>{repo}</span>
      {branch ? <span style={s.pillBranch}>{branch}</span> : null}
    </span>
  );
}

const fullFmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
const when = (iso: string | null) => (iso ? fullFmt.format(Date.parse(iso)) : 'Not yet');

/** Everything about a job the table leaves out. */
export function JobDetails({
  job,
  extra = [],
  onClose,
  onAnalyse,
}: {
  job: RunJob;
  /** What only the panel it was opened from knows, e.g. how long a queued job has waited. Shown after the runner. */
  extra?: [string, ReactNode][];
  onClose: () => void;
  onAnalyse?: (job: RunJob) => void;
}) {
  // `outcomeOf` calls anything not completed "Running", which is right on the
  // chart and wrong for a job the queue shows still waiting for a runner.
  const waiting = job.status !== 'completed' && job.started_at === null;
  const rows: [string, ReactNode][] = [
    ['Outcome', waiting ? <span key="o" style={s.waiting}>Waiting for a runner</span> : <OutcomeLabel key="o" outcome={outcomeOf(job)} />],
    ['GitHub status', `${job.status}${job.conclusion ? `, ${job.conclusion}` : ''}`],
    ['Repo and branch', <RepoBranch key="r" repo={job.repo} branch={job.head_branch} />],
    ['Workflow', job.workflow],
    ['Job', job.job_name],
    ['Trigger', job.event ? triggerLabel(job.event) : 'Not recorded'],
    ['Started by', job.actor ?? 'Not recorded'],
    ['Runner', job.runner_name ?? 'Not assigned'],
    ['Runner type', job.kind ? (KIND_LABEL[job.kind] ?? job.kind) : 'Not assigned'],
    ...extra,
    ['Queued', when(job.created_at)],
    ['Started', when(job.started_at)],
    ['Finished', when(job.completed_at)],
    ['Wait for a runner', seconds(job.queue_s)],
    ['Run time', seconds(job.duration_s)],
    ['Attempt', String(job.run_attempt)],
    ['Job id', <code key="j">{job.id}</code>],
    ['Run id', <code key="u">{job.run_id}</code>],
  ];

  return (
    <Modal
      title={`${job.workflow} · ${job.job_name}`}
      subtitle={job.title ?? undefined}
      onClose={onClose}
      width={560}
      footer={
        <ModalActions>
          {onAnalyse ? (
            <Button
              onClick={() => {
                onClose();
                onAnalyse(job);
              }}
            >
              Analyse this job
            </Button>
          ) : null}
          <a href={jobUrl(job)} target="_blank" rel="noopener noreferrer" style={s.linkButton}>
            Open on GitHub ↗
          </a>
        </ModalActions>
      }
    >
      <dl style={s.details}>
        {rows.map(([label, value]) => (
          <div key={label} style={s.detailRow}>
            <dt style={s.detailLabel}>{label}</dt>
            <dd style={s.detailValue}>{value}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}

const s = {
  waiting: { fontSize: '.8rem', fontWeight: 600, color: C.textMid },
  pill: {
    display: 'inline-flex',
    alignItems: 'stretch',
    maxWidth: '100%',
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    overflow: 'hidden',
    fontSize: '.75rem',
    lineHeight: 1.6,
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'middle',
  },
  pillRepo: { padding: '0 .55rem', background: C.border3, color: C.textMid, fontWeight: 600 },
  pillBranch: {
    padding: '0 .55rem',
    borderLeft: `1px solid ${C.border}`,
    color: C.textMid2,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  details: { display: 'grid', gap: '.1rem', margin: 0 },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '9.5rem minmax(0, 1fr)',
    gap: '1rem',
    padding: '.4rem 0',
    borderBottom: `1px solid ${C.border2}`,
    alignItems: 'center',
  },
  detailLabel: { fontSize: '.8rem', color: C.muted },
  detailValue: { margin: 0, fontSize: '.85rem', color: C.text, overflowWrap: 'anywhere' as const },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '.45rem .85rem',
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.surface,
    color: C.text,
    fontSize: '.85rem',
    fontWeight: 600,
    textDecoration: 'none',
  },
};
